import { Box, Button, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useEventsApi } from "../hooks/useEventsApi";
import { config } from "../config";
import CustomAlert from "./CustomAlert";

interface ExportHmiButtonProps {
  onStart: () => void;
  onEnd: () => void;
  targetSystem: string;
  buttonText: string;
  disabledExternally?: boolean;
  /** Appended as query string on POST start-export-… (e.g. mode, use_old_code, prefix) */
  startQueryParams?: Record<string, string>;
  /** If set, used as the browser download filename instead of the default per targetSystem */
  downloadFileName?: string;
  /** Show "Export " prefix before buttonText (default true). */
  showExportPrefix?: boolean;
}

function toDownloadBlob(data: unknown): Blob {
  if (data instanceof Blob) return data;
  const json =
    typeof data === "string" ? data : JSON.stringify(data, null, 2);
  return new Blob([json.endsWith("\n") ? json : `${json}\n`], {
    type: "application/json",
  });
}

const ExportHmiButton: React.FC<ExportHmiButtonProps> = ({
  targetSystem,
  buttonText,
  onStart,
  onEnd,
  disabledExternally,
  startQueryParams,
  downloadFileName,
  showExportPrefix = true,
}) => {
  const { callApi } = useEventsApi();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    onStart();
    try {
      const qs = startQueryParams
        ? `?${new URLSearchParams(startQueryParams).toString()}`
        : "";
      // 1. Export starten
      const startResponse = await callApi(
        `${config.apiUrl}/api/start-export-${targetSystem}/${qs}`,
        "POST"
      );

      const export_id = startResponse?.export_id;
      if (!export_id) {
        throw new Error("Export could not be started.");
      }

      // 2. Polling bis Datei verfügbar ist
      let attempts = 0;
      const maxAttempts = 20;
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      let zipBlob: Blob | null = null;

      while (attempts < maxAttempts) {
        await delay(3000);

        try {
          const data = await callApi(
            `${config.apiUrl}/api/download-export-${targetSystem}/${export_id}/`,
            "GET"
          );
          zipBlob = toDownloadBlob(data);
          if (zipBlob.size < 1) {
            console.warn(`Versuch ${attempts + 1}: Download empty, retrying`);
            zipBlob = null;
            attempts++;
            continue;
          }
          break;
        } catch (err: any) {
          if (err?.status && err.status !== 404) {
            throw err;
          }
          console.warn(`Versuch ${attempts + 1}: Export not available`, err);
        }

        attempts++;
      }

      if (!zipBlob) {
        throw new Error("Export not available or took too long.");
      }

      // 3. Datei herunterladen
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      if (downloadFileName) {
        link.setAttribute("download", downloadFileName);
      } else if (targetSystem === "visuplus")
        link.setAttribute("download", `events_export_${targetSystem}.zip`);
      else if (targetSystem === "alarms-xml")
        link.setAttribute("download", `events_export_${targetSystem}.xml`);
      else if (targetSystem === "webhmi")
        link.setAttribute("download", "events_webhmi.json");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
      setAlertMessage(`Export failed: ${String(error)}`);
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      onEnd();
    }
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("error");

  return (
    <Box>
      <Button
        onClick={handleExport}
        disabled={loading || disabledExternally}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {`${showExportPrefix ? "Export " : ""}${buttonText}`.trim()}
      </Button>
      <CustomAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        severity={alertSeverity}
      />
    </Box>
  );
};

export default ExportHmiButton